
import { useEffect, useRef, useState } from 'react';
import { useNotificationStore } from './store';
import { useEntityRealtime } from '@/lib/realtime';
import { useOrganizationStore } from '@/store/organization.store';
import {
  NotificationQueryDto,
  NotificationType,
  NotificationChannel,
  Notification,
} from '@scrambl/shared';
import {
  useNotificationsQuery,
  useUnreadNotificationCountQuery,
  useNotificationPreferencesQuery,
} from '@/hooks/queries/useNotificationsQuery';
import {
  useMarkNotificationAsReadMutation,
  useMarkMultipleNotificationsAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDismissNotificationMutation,
  useDismissMultipleNotificationsMutation,
  useUpdateNotificationPreferenceMutation,
  useUpdateMultipleNotificationPreferencesMutation,
  useSetDefaultNotificationPreferencesMutation,
} from '@/hooks/mutations/useNotificationsMutations';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/query-keys';
import { browserNotificationService } from '@/services/browserNotificationService';

// Hook to manage notifications with real-time updates
export const useNotifications = (autoFetch = true) => {
  const organizationId = useOrganizationStore(
    (state) => state.currentOrganization?.id,
  );
  const { addEventListener, isConnected } = useEntityRealtime('organization', organizationId ?? null, {
    suppressLogging: true,
  });
  const queryClient = useQueryClient();
  const {
    handleNotificationCreated,
    handleNotificationUpdated,
    handleNotificationDeleted,
    handleUnreadCountUpdated,
  } = useNotificationStore();

  // State for filters and accumulated notifications
  const [filters, setFilters] = useState<NotificationQueryDto & { page?: number; limit?: number }>({
    page: 1,
    limit: 20,
  });
  const [accumulatedNotifications, setAccumulatedNotifications] = useState<Notification[]>([]);
  const recentNotificationIdsRef = useRef<Map<string, number>>(new Map());

  // Query for notifications
  const { data: notificationsData, isLoading } = useNotificationsQuery(
    filters,
    { enabled: autoFetch && !!organizationId }
  );

  // Unread count query
  const { data: unreadCountData } = useUnreadNotificationCountQuery({ enabled: autoFetch && !!organizationId });

  // Accumulate notifications across pages
  useEffect(() => {
    if (notificationsData?.data) {
      if (notificationsData.page === 1) {
        // First page - replace all notifications
        setAccumulatedNotifications(notificationsData.data);
      } else {
        // Subsequent pages - append to existing
        setAccumulatedNotifications((prev) => [...prev, ...notificationsData.data]);
      }
    }
  }, [notificationsData]);

  const notifications = accumulatedNotifications;
  const unreadCount = unreadCountData?.count || 0;
  const hasMore = notificationsData ? notificationsData.page < notificationsData.totalPages : false;

  // Mutations
  const markAsReadMutation = useMarkNotificationAsReadMutation();
  const markMultipleAsReadMutation = useMarkMultipleNotificationsAsReadMutation();
  const markAllAsReadMutation = useMarkAllNotificationsAsReadMutation();
  const dismissNotificationMutation = useDismissNotificationMutation();
  const dismissMultipleMutation = useDismissMultipleNotificationsMutation();

  // Socket event listeners - use direct cache updates instead of invalidation for efficiency
  useEffect(() => {
    if (!organizationId || !isConnected) return;

    const resolveOrganizationId = (data: Record<string, unknown>) => {
      const typed = data as { organizationId?: string; notification?: { organizationId?: string } };
      return typed.organizationId ?? typed.notification?.organizationId ?? organizationId ?? undefined;
    };

    const handleCreated = (data: { notification: Notification }) => {
        const notificationId = data.notification?.id;
        if (notificationId) {
          const now = Date.now();
          const lastSeen = recentNotificationIdsRef.current.get(notificationId);
          if (lastSeen && now - lastSeen < 5000) {
            return;
          }
          recentNotificationIdsRef.current.set(notificationId, now);
          if (recentNotificationIdsRef.current.size > 500) {
            for (const [id, ts] of recentNotificationIdsRef.current) {
              if (now - ts > 60000) {
                recentNotificationIdsRef.current.delete(id);
              }
            }
          }
        }
        handleNotificationCreated(data.notification);
        // Prepend new notification to the list cache directly
        queryClient.setQueriesData<{ data: Notification[]; total: number; page: number; limit: number; totalPages: number }>(
          { queryKey: queryKeys.notifications.lists() },
          (old) => {
            if (!old) return old;
            // Only update first page cache, prepend new notification
            if (old.page === 1) {
              if (notificationId && old.data.some((n) => n.id === notificationId)) {
                return old;
              }
              return {
                ...old,
                data: [data.notification, ...old.data.slice(0, old.limit - 1)],
                total: old.total + 1,
              };
            }
            return old;
          }
        );
        // Also add to accumulated notifications state
        setAccumulatedNotifications((prev) => {
          if (notificationId && prev.some((n) => n.id === notificationId)) {
            return prev;
          }
          return [data.notification, ...prev];
        });
    };
    const handleUpdated = (data: {
        notificationId: string;
        updates: Partial<Notification>;
      }) => {
        handleNotificationUpdated(data.notificationId, data.updates);
        // Update notification in cache directly
        queryClient.setQueriesData<{ data: Notification[]; total: number; page: number; limit: number; totalPages: number }>(
          { queryKey: queryKeys.notifications.lists() },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data.map((n) =>
                n.id === data.notificationId ? { ...n, ...data.updates } : n
              ),
            };
          }
        );
        // Update accumulated notifications state
        setAccumulatedNotifications((prev) =>
          prev.map((n) =>
            n.id === data.notificationId ? { ...n, ...data.updates } : n
          )
        );
    };
    const handleDeleted = (data: { notificationId: string }) => {
        handleNotificationDeleted(data.notificationId);
        // Remove notification from cache directly
        queryClient.setQueriesData<{ data: Notification[]; total: number; page: number; limit: number; totalPages: number }>(
          { queryKey: queryKeys.notifications.lists() },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data.filter((n) => n.id !== data.notificationId),
              total: Math.max(0, old.total - 1),
            };
          }
        );
        // Update accumulated notifications state
        setAccumulatedNotifications((prev) =>
          prev.filter((n) => n.id !== data.notificationId)
        );
    };
    const handleUnreadCount = (data: { count: number }) => {
        handleUnreadCountUpdated(data.count);
        // Update query cache directly (no invalidation needed)
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), { count: data.count });
    };

    const cleanupCreated = addEventListener('NOTIFICATION_CREATED', handleCreated, {
      resolveEntityId: resolveOrganizationId,
    });
    const cleanupUpdated = addEventListener('NOTIFICATION_UPDATED', handleUpdated, {
      resolveEntityId: resolveOrganizationId,
    });
    const cleanupDeleted = addEventListener('NOTIFICATION_DELETED', handleDeleted, {
      resolveEntityId: resolveOrganizationId,
    });
    const cleanupUnread = addEventListener('UNREAD_COUNT_UPDATED', handleUnreadCount, {
      resolveEntityId: resolveOrganizationId,
    });

    // Cleanup
    return () => {
      cleanupCreated();
      cleanupUpdated();
      cleanupDeleted();
      cleanupUnread();
    };
  }, [
    addEventListener,
    isConnected,
    organizationId,
    queryClient,
    handleNotificationCreated,
    handleNotificationUpdated,
    handleNotificationDeleted,
    handleUnreadCountUpdated,
  ]);

  const fetchNotifications = async (query?: NotificationQueryDto) => {
    setAccumulatedNotifications([]); // Clear accumulated notifications when filters change
    setFilters((prev) => ({
      ...prev,
      ...query,
      page: 1, // Reset to first page when filters change
    }));
    // Query will automatically refetch when filters change
  };

  const fetchMoreNotifications = async () => {
    if (hasMore && !isLoading) {
      setFilters((prev) => ({
        ...prev,
        page: (prev.page || 1) + 1,
      }));
      // Query will automatically refetch when filters change
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    error: null, // Errors handled by TanStack Query
    hasMore,
    fetchNotifications,
    fetchMoreNotifications,
    fetchUnreadCount: () => {}, // Handled automatically by query
    markAsRead: markAsReadMutation.mutateAsync,
    markMultipleAsRead: markMultipleAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    dismissNotification: dismissNotificationMutation.mutateAsync,
    dismissMultiple: dismissMultipleMutation.mutateAsync,
  };
};

// Hook for notification preferences
export const useNotificationPreferences = () => {
  const { currentOrganization } = useOrganizationStore();
  const { data: preferences = [] } = useNotificationPreferencesQuery({
    enabled: !!currentOrganization?.id,
  });

  const {
    updatePreference: updatePreferenceMutation,
    updateMultiplePreferences: updateMultiplePreferencesMutation,
    setDefaultPreferences: setDefaultPreferencesMutation,
  } = useNotificationPreferencesMutations();

  const isPreferenceEnabled = (channel: NotificationChannel, type: NotificationType) => {
    const preference = preferences.find(
      (p) => p.channel === channel && p.type === type,
    );
    return preference?.enabled ?? true; // Default to enabled if no preference
  };

  const togglePreference = async (channel: NotificationChannel, type: NotificationType) => {
    const currentEnabled = isPreferenceEnabled(channel, type);
    await updatePreferenceMutation.mutateAsync({
      channel,
      type,
      enabled: !currentEnabled,
    });
  };

  return {
    preferences,
    isPreferenceEnabled,
    togglePreference,
    updatePreference: updatePreferenceMutation.mutateAsync,
    updateMultiplePreferences: updateMultiplePreferencesMutation.mutateAsync,
    setDefaultPreferences: setDefaultPreferencesMutation.mutateAsync,
  };
};

// Hook for unread count badge
export const useUnreadNotificationCount = () => {
  const organizationId = useOrganizationStore(
    (state) => state.currentOrganization?.id,
  );
  const { addEventListener, isConnected } = useEntityRealtime('organization', organizationId ?? null, {
    suppressLogging: true,
  });
  const queryClient = useQueryClient();
  const { data: unreadCountData } = useUnreadNotificationCountQuery({
    enabled: !!organizationId,
  });
  const { handleUnreadCountUpdated } = useNotificationStore();

  const count = unreadCountData?.count || 0;

  // Sync desktop badge with unread count
  useEffect(() => {
    if (browserNotificationService.isDesktopApp()) {
      browserNotificationService.setBadgeCount(count);
    }
  }, [count]);

  useEffect(() => {
    if (!organizationId || !isConnected) return;

    const handleUnreadUpdate = (data: { count: number }) => {
      handleUnreadCountUpdated(data.count);
      queryClient.setQueryData(queryKeys.notifications.unreadCount(), { count: data.count });

      // Update desktop badge when unread count changes via WebSocket
      if (browserNotificationService.isDesktopApp()) {
        browserNotificationService.setBadgeCount(data.count);
      }
    };

    const cleanupUnread = addEventListener('UNREAD_COUNT_UPDATED', handleUnreadUpdate, {
      resolveEntityId: (data) => {
        const typed = data as { organizationId?: string };
        return typed.organizationId ?? organizationId ?? undefined;
      },
    });

    return () => {
      cleanupUnread();
    };
  }, [addEventListener, isConnected, organizationId, queryClient, handleUnreadCountUpdated]);

  return count;
};

// Hook for filtering notifications
export const useFilteredNotifications = (filters: NotificationQueryDto) => {
  const { data: notificationsData, isLoading } = useNotificationsQuery(filters);

  return {
    notifications: notificationsData?.data || [],
    isLoading,
  };
};

// Hook for notification actions
export const useNotificationActions = () => {
  const markAsReadMutation = useMarkNotificationAsReadMutation();
  const markMultipleAsReadMutation = useMarkMultipleNotificationsAsReadMutation();
  const markAllAsReadMutation = useMarkAllNotificationsAsReadMutation();
  const dismissNotificationMutation = useDismissNotificationMutation();
  const dismissMultipleMutation = useDismissMultipleNotificationsMutation();

  const markNotificationAsRead = async (notificationId: string) => {
    await markAsReadMutation.mutateAsync(notificationId);
  };

  const markAllNotificationsAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
  };

  const dismissNotificationById = async (notificationId: string) => {
    await dismissNotificationMutation.mutateAsync(notificationId);
  };

  return {
    markNotificationAsRead,
    markAllNotificationsAsRead,
    dismissNotificationById,
    markMultipleAsRead: markMultipleAsReadMutation.mutateAsync,
    dismissMultiple: dismissMultipleMutation.mutateAsync,
  };
};

// Helper hook for notification preferences mutations
function useNotificationPreferencesMutations() {
  return {
    updatePreference: useUpdateNotificationPreferenceMutation(),
    updateMultiplePreferences: useUpdateMultipleNotificationPreferencesMutation(),
    setDefaultPreferences: useSetDefaultNotificationPreferencesMutation(),
  };
}
